/**
 * controller init function for WixComments component
 * @module controller
 */
import { mapValues, omitBy } from 'lodash';
import { bindActionCreators } from 'redux';
import { setBaseParams, updateInstance } from '../common/store/base-params/base-params-actions';
import { fetchTranslations } from '../common/store/translations/translations-actions';
import { WIX_CHAT, MEMBERS_AREA } from '@wix/app-definition-ids';
import { validateLanguage } from './utils/validate-language';
import { createInstanceManager } from './utils/get-instance';
import { fetchExperiments } from '../common/store/experiments/experiments-actions';
import { biVisitMemberProfile } from '../common/store/actions/bi-actions';
import { refetchCommentsData } from '../common/store/comments/paging/comments-paging-actions';
import { createSentryMonitor, decorateActionsWithLogger } from './utils/monitoring';
import { handleError } from './utils/handle-error';
import { requestLoginPromisified } from '../common/store/actions/request-login';
import { getIsMemberCheckPending } from '../common/store/members/members-selectors';
import { initBiMiddleware } from '../common/store/middleware/bi-middleware';
import { initMaNavigation } from '../common/store/ma-navigation/ma-navigation-actions';
import { initDevToolsStore } from '../component/components/dev-panel/init-dev-tool-store';
import { createStateOptimizer } from './utils/state-optimizer';
import { createPaginationWatcher } from '../common/store/comments/paging/comments-paging-watcher';
import { createTranslationsWatcher } from '../common/store/translations/translations-watcher';
import { createRatingsWatcher } from '../common/store/comments/ratings/ratings-watcher';
import { createCommentsWatcherConfig } from '../common/store/comments/created-comments-watcher';
import { initBeforeCrud } from '../common/store/comments/comments-before-crud-interceptor';
import { controllerActions } from './controller-actions';
import { resolveTestMode } from './utils/resolve-test-mode';
import { createCommentsStore } from '../common/store/create-comments-store';
import { createStateCache } from './create-state-cache';
export * from './controller-public-types';
export { Ordering, SortOrder } from '../common/common-enums';
/**
 * @param controllerConfig - provided by the platform: wixCodeApi, platformAPIs, setProps, compId. Pass `controllerContex.flowApi.contorllerConfig` from your controller paramters
 * @param options - configure comments controller
 *
 *  @example
 *  ```typescript
 * // initialize the controller inside your worker
 * import { initializeCommentsController } from '@wix/comments-ooi-client/controller';
 *
 * async pageReady() {
 *   const commentsApi = await initializeCommentsController(flowAPI.controllerConfig, {
 *     appDefinitionId: 'YOUR-APP-DEF-ID',
 *   });
 * }
 *```
 * After controller is initialized it returns API that can be used for advanced functionality.
 */
export async function initializeCommentsController(controllerConfig, { appDefinitionId, shouldAutoBindStateToSetProps = true, actionPreconditions, liveUpdates, testMode: inputTestMode, logNetworkErrorsInConsole = false, httpClient, mocks, _persistDraft, enableWarmupDataCache, }) {
    const { wixCodeApi, platformAPIs, setProps, compId } = controllerConfig;
    const isSSR = wixCodeApi.window.rendering.env === 'backend';
    const viewMode = wixCodeApi.window.viewMode;
    const sentry = createSentryMonitor({ isSSR, platformAPIs });
    let store;
    let actions;
    const stateCache = createStateCache({ wixCodeApi });
    const testMode = resolveTestMode(inputTestMode);
    const invokeAppCrash = () => {
        const dummyError = new Error(DUMMY_ERROR);
        handleError({ store, isSSR, setProps, actions, sentry })(dummyError);
    };
    const isDebug = !!wixCodeApi.location.query.debug;
    const devToolsApi = initDevToolsStore({
        onChange: (state) => {
            if (state.specialStates === 'crash') {
                invokeAppCrash();
            }
            setProps({ wcDevToolsState: state });
        },
        wixCodeApi,
        isDebug,
    });
    if (devToolsApi.getState().specialStates === 'crash') {
        invokeAppCrash();
    }
    const commentsWatcherConfig = createCommentsWatcherConfig();
    const beforeCreateInterceptor = initBeforeCrud();
    try {
        if (isSSR && testMode.crashIn === 'SSR') {
            throw new Error('Damn, unlucky crash!');
        }
        const language = validateLanguage(devToolsApi.getState().language ||
            (wixCodeApi.window.multilingual.isEnabled &&
                wixCodeApi.window?.multilingual?.currentLanguage) ||
            wixCodeApi.site.language);
        const instanceManager = createInstanceManager(wixCodeApi, appDefinitionId);
        const biMiddleware = initBiMiddleware(platformAPIs, instanceManager);
        store = createCommentsStore({
            wixCodeApi,
            platformAPIs,
            appDefinitionId,
            compId,
            biMiddleware,
            actionPreconditions,
            isSSR,
            duplexerClient: liveUpdates?.duplexerClient,
            instanceManager,
            sentry,
            devToolsApi,
            testMode,
            commentsWatcherMidlleware: commentsWatcherConfig.middleware,
            executeBeforeCrud: beforeCreateInterceptor.executeBeforeCrud,
            logNetworkErrorsInConsole,
            httpClient,
            persistDraft: _persistDraft,
            mocks,
            preloadedState: !!enableWarmupDataCache && !isSSR ? stateCache.getState() : undefined,
        });
        // handle app token refresh
        instanceManager.onChange(({ instance }) => {
            store?.dispatch(updateInstance({ instance }));
        });
        await Promise.all([
            store.dispatch(fetchTranslations(language)),
            initUserDependentStoreBaseData({
                store,
                appDefinitionId,
                language,
                isSSR,
            }),
        ]);
        actions = initializeActions({
            wixCodeApi,
            store,
            sentry,
            logErrorsToConsole: isDebug || logNetworkErrorsInConsole,
        });
        refreshDataOnLogin({
            wixCodeApi,
            store,
            appDefinitionId,
            language,
        });
    }
    catch (error) {
        handleError({ store, isSSR, setProps, actions, sentry })(error);
    }
    const bindStateToSetProps = () => {
        if (store && actions) {
            if (isSSR) {
                const successFlags = mapValues(store.getState().comments, (resourceState) => resourceState?.type === 'READY');
                controllerConfig.wixCodeApi.window.warmupData.set('WC_SSR_LOAD', successFlags);
            }
            const stateOptimizer = createStateOptimizer({
                store,
                onChange: (wcState) => setProps({ wcState }),
                disablePatching: isSSR || viewMode === 'Editor',
            });
            if (enableWarmupDataCache && isSSR) {
                const state = store.getState();
                stateCache.setState({
                    ...state,
                    comments: omitBy(state.comments, (resourceState) => resourceState?.type !== 'READY'),
                });
            }
            setProps({
                wcState: {
                    type: 'SET',
                    state: store.getState(),
                },
                ...actions,
                wcDevToolsApi: devToolsApi,
                wcDevToolsState: devToolsApi.getState(),
                wcRequestFullState: stateOptimizer.requestFullState,
                wcSsrLoadedCorrectly: isSSR
                    ? mapValues(store.getState().comments, () => true)
                    : controllerConfig.wixCodeApi.window.warmupData.get('WC_SSR_LOAD'),
            });
            stateOptimizer.subscribe();
        }
        else {
            // The error should already be logged by 'handleError' above
        }
    };
    if (shouldAutoBindStateToSetProps) {
        bindStateToSetProps();
    }
    return {
        // api (to be consumed from controller)
        fetchComments: ({ resourceId, ctxFields, pagination, withVotes = false, withRatings = false, }) => actions &&
            actions.wcActions.fetchComments({
                resourceId,
                ctxFields,
                pagination,
                withVotes: resolveVotesWithDevState({
                    withVotes,
                    reactionsDevState: devToolsApi.getState().commentReactions,
                }),
                withRatings: resolveRatingsWithDevState({
                    withRatings,
                    ratingsDevState: devToolsApi.getState().ratings,
                }),
            }),
        bulkFetchComments: (payload) => actions &&
            actions.wcActions.bulkFetchComments(payload.map(({ resourceId, ctxFields, pagination, withVotes = false, withRatings = false }) => ({
                resourceId,
                ctxFields,
                pagination,
                withVotes: resolveVotesWithDevState({
                    withVotes,
                    reactionsDevState: devToolsApi.getState().commentReactions,
                }),
                withRatings: resolveRatingsWithDevState({
                    withRatings,
                    ratingsDevState: devToolsApi.getState().ratings,
                }),
            }))),
        fetchDeepLink: ({ commentId, resourceId, pagination, withVotes = false, withRatings = false, ctxFields, }) => actions &&
            actions.wcActions.fetchDeepLink({
                ctxFields,
                resourceId,
                pagination,
                commentId,
                withVotes: resolveVotesWithDevState({
                    withVotes,
                    reactionsDevState: devToolsApi.getState().commentReactions,
                }),
                withRatings: resolveRatingsWithDevState({
                    withRatings,
                    ratingsDevState: devToolsApi.getState().ratings,
                }),
            }),
        permissionsChangedExternally: () => {
            actions && actions.wcActions.refetchCommentsData();
        },
        bindStateToSetProps: shouldAutoBindStateToSetProps
            ? () => console.error('Please set `shouldAutoBindStateToSetProps` to `false` if you need to use `bindStateToSetProps`')
            : bindStateToSetProps,
        watch: {
            pagination: createPaginationWatcher(store),
            translations: createTranslationsWatcher(store),
            comments: commentsWatcherConfig.createWatcher(store),
            ratings: createRatingsWatcher(store),
        },
        intercept: {
            onBeforeCrud: beforeCreateInterceptor.onBeforeCrud,
        },
        affect: {
            tryOpenCommentBox: (resourceId, options) => actions && actions.wcActions.tryOpenCommentBox({ resourceId, ...options }),
            tryCloseAllCommentBoxes: (resourceId) => actions && actions.wcActions.tryCloseAllCommentBoxes({ resourceId }),
        },
        unmountAllResources: () => actions && actions.wcActions.unmountAllResources(),
    };
}
function initializeActions({ wixCodeApi, store, sentry, logErrorsToConsole, }) {
    return {
        wcActions: decorateActionsWithLogger({
            ...bindActionCreators(controllerActions, store.dispatch),
            startPrivateChat: (uid) => wixCodeApi.site.getPublicAPI(WIX_CHAT).then((api) => api.startPrivateChat(uid)),
            navigateToMember: (memberId, memberSlug, resourceId) => {
                store.dispatch(biVisitMemberProfile({ memberId, resourceId }));
                wixCodeApi.site
                    .getPublicAPI(MEMBERS_AREA)
                    .then((api) => api.navigateToMember({ memberId, memberSlug }));
            },
            navigateToSection: (section) => wixCodeApi.site.getPublicAPI(MEMBERS_AREA).then((api) => api.navigateToSection(section)),
            requestLogout: () => {
                wixCodeApi.user.logout();
            },
        }, sentry, logErrorsToConsole),
        wcActionsPromisified: decorateActionsWithLogger(bindActionCreators({ requestLoginPromisified }, store.dispatch), sentry, logErrorsToConsole),
    };
}
function initUserDependentStoreBaseData({ store, appDefinitionId, language, isSSR = false, }) {
    return Promise.all([
        store.dispatch(setBaseParams({ appDefinitionId, language, isSSR })).then(() => {
            store.dispatch(fetchExperiments());
            store.dispatch(initMaNavigation());
        }),
    ]);
}
function refreshDataOnLogin({ wixCodeApi, store, appDefinitionId, language, }) {
    wixCodeApi.user.onLogin(() => {
        setTimeout(() => {
            initUserDependentStoreBaseData({ store, appDefinitionId, language });
            !getIsMemberCheckPending(store.getState()) && store.dispatch(refetchCommentsData());
        });
    });
}
const resolveVotesWithDevState = ({ withVotes, reactionsDevState, }) => {
    return reactionsDevState === 'auto' ? withVotes : reactionsDevState === 'votes' ? true : false;
};
const resolveRatingsWithDevState = ({ withRatings, ratingsDevState, }) => {
    return ratingsDevState.type === 'auto'
        ? withRatings
        : ratingsDevState.type === 'on'
            ? true
            : false;
};
/** @internal */
export const DUMMY_ERROR = 'This is a dummy error';
//# sourceMappingURL=controller.js.map